// src/hooks/useDentallyIntegrationScope.js
//
// One answer to "may this practice's Dentally UI render right now?" for the
// current route practice. Mirrors the scoping in AppointmentBookingRouter:
// a persisted global boolean alone is not enough during practice switches,
// so the permission snapshot and the integration config must both belong to
// the expected practice and be settled.
//
//   visible   – show read-only Dentally information (view or manage access,
//               active integration for THIS practice, config load settled).
//   canManage – additionally allowed to perform write actions (link, book…).
//   site      – configured Dentally site { id, name } for the active config, or
//               null (all sites / single-site account / not yet loaded).
//   ready     – permission + config state is settled for this practice; while
//               false, callers should render nothing (or a neutral loading state)
//               rather than "not linked" / "inactive".
//   pending   – the Dentally answer for this practice is still unknown AND could
//               still turn out to be "yes": the permission snapshot has not
//               settled, or it has and the feature is viewable but the config
//               has not resolved. Callers that render a Dentally variant next to
//               a CRM-only variant (the booking wizard, the shared personal
//               details form) show a neutral placeholder while this is true so
//               they never paint the CRM variant and swap it a moment later.
//               It is deliberately NOT `!ready`: for a practice whose permission
//               snapshot says the feature is hidden, nothing ever requests the
//               config, so `ready` stays false forever while `pending` is false
//               and the CRM variant renders immediately, which is correct.
import { useSelector } from '../redux/store';
import { siteFromConfig } from '../utils/dentallySite';

const asId = (value) => (value == null || value === '' ? null : String(value));

export const DENTALLY_FEATURE = 'dentally-integration';

export default function useDentallyIntegrationScope(practiceId) {
  const permissionsState = useSelector((state) => state.permissions || {});
  const dentallyEnabledGlobal = useSelector((state) => state.practice?.dentallyEnabledGlobal === true);
  const integration = useSelector((state) => state.practiceIntegration || {});

  const expectedPractice = asId(practiceId);
  const permissionPractice = asId(permissionsState.practiceId);
  const permissionTarget = asId(permissionsState.loadingPracticeId);
  const permissionScopeCurrent = Boolean(expectedPractice)
    && !permissionsState.loading
    && permissionPractice === expectedPractice
    && (!permissionTarget || permissionTarget === expectedPractice);
  const level = permissionScopeCurrent ? permissionsState.permissions?.[DENTALLY_FEATURE] : undefined;
  const canView = level === 'view' || level === 'manage';
  const canManage = level === 'manage';

  const configScopeCurrent = Boolean(expectedPractice) && asId(integration.dentallyConfigPracticeId) === expectedPractice;
  const configResults = integration.dentallyConfigs?.results || [];
  const activeConfig = configScopeCurrent
    ? configResults.find((config) => config?.is_active === true) || null
    : null;
  let configLoadState = 'not_requested';
  if (configScopeCurrent) {
    if (integration.dentallyConfigLoadState) configLoadState = integration.dentallyConfigLoadState;
    else if (integration.dentallyLoading) configLoadState = 'loading';
    else if (integration.dentallyConfigs) configLoadState = 'loaded';
  }
  const configSettled = ['loaded', 'forbidden', 'error'].includes(configLoadState);
  const ready = permissionScopeCurrent && configSettled;
  const active = Boolean(dentallyEnabledGlobal && configLoadState === 'loaded' && activeConfig);
  const pending = Boolean(expectedPractice) && (!permissionScopeCurrent || (canView && !configSettled));

  return {
    practiceId: expectedPractice,
    ready,
    pending,
    active,
    canView,
    canManage,
    visible: ready && active && canView,
    configLoadState,
    activeConfigId: activeConfig?.id ?? null,
    site: siteFromConfig(activeConfig),
  };
}
